import { useMemo, useState } from 'react';

import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { Select } from '@bambi/component-library/src/components/inputs/Select/Select';
import { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { DateTime } from 'luxon';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import type { RootState } from 'src/app/store/types';
import { Alert } from 'src/common/Alert/Alert';
import { useVehiclesListQuery } from 'src/common/external/bambi-api/bambiApi';
import { FormFieldController } from 'src/common/FormField/FormFieldController';

import { setSelectedVehicle } from '../../assignmentManagement.slice';
import { TransferAssignmentAlert } from './TransferAssignmentAlert';

export function SchedulableVehicleSelect() {
  const dispatch = useAppDispatch();
  const [showVehicleChangeWarning, setShowVehicleChangeWarning] =
    useState(false);

  const { selectedAssignment } = useAppSelector(
    (state: RootState) => state.assignmentManagement
  );
  const { data: vehicles } = useVehiclesListQuery({
    canBeScheduled: true,
  });

  const vehicleOptions = useMemo(
    () =>
      (vehicles?.results ?? []).map((vehicle) => ({
        label: vehicle.nickname || vehicle.id,
        value: vehicle.id,
      })),
    [vehicles?.results]
  );

  const isAssignmentStartInThePast = selectedAssignment?.start
    ? DateTime.fromISO(selectedAssignment.start) < DateTime.now()
    : false;

  const disableVehicleSelection =
    !!selectedAssignment && isAssignmentStartInThePast;

  const handleVehicleChange = (
    field: any,
    selected: SelectOption<string> | undefined
  ) => {
    const vehicle = (vehicles?.results ?? []).find(
      (vehicle) => vehicle.id === selected?.value
    );

    if (
      selectedAssignment &&
      vehicle?.id !== selectedAssignment.vehicle.vehicle
    ) {
      setShowVehicleChangeWarning(true);
    } else {
      setShowVehicleChangeWarning(false);
    }

    field.onChange(vehicle);
    dispatch(setSelectedVehicle(vehicle));
  };

  return (
    <>
      {showVehicleChangeWarning && (
        <div className="max-w-lg">
          <Alert
            title="Vehicle Changed"
            message="By changing the vehicle, you are confirming that the new vehicle meets all the requirements for any assigned trips."
            severity="warning"
          />
        </div>
      )}
      {disableVehicleSelection ? <TransferAssignmentAlert /> : null}
      <FormFieldController
        name="vehicle"
        rules={{ required: 'Please choose a vehicle' }}
      >
        {({ field, fieldState }) => {
          return (
            <DefaultFormFieldLayout
              label="Vehicle"
              error={fieldState.error?.message?.toString()}
              inputId="vehicle"
              helperText={
                disableVehicleSelection
                  ? {
                      text: 'Vehicle cannot be changed after the crew schedule has started',
                      presentation: {
                        type: 'tooltip',
                      },
                    }
                  : undefined
              }
            >
              <Select
                options={vehicleOptions}
                onChange={(selected) => {
                  handleVehicleChange(field, selected);
                }}
                value={field.value?.id}
                disabled={disableVehicleSelection}
                error={fieldState.error?.message?.toString()}
              />
            </DefaultFormFieldLayout>
          );
        }}
      </FormFieldController>
    </>
  );
}
